import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import { DashboardStats } from '../../models';
import { dashboardApi } from '../../services/api';
import {
    People as PeopleIcon,
    Group as GroupIcon,
    PersonAdd as PersonAddIcon,
    Assignment as AssignmentIcon,
    Error as ErrorIcon,
    Pending as PendingIcon,
    Sync as SyncIcon,
    SportsScore as ScoreIcon,
    PhoneAndroid as PhoneIcon,
    PhoneIphone as MobileIcon,
    LockOpen as OtpIcon
} from '@mui/icons-material';

const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
}> = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
                <Box
                    sx={{
                        backgroundColor: `${color}20`,
                        borderRadius: '50%',
                        p: 1,
                        mr: 2,
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="h6" component="div">
                    {title}
                </Typography>
            </Box>
            <Typography variant="h4" component="div">
                {value.toLocaleString()}
            </Typography>
        </CardContent>
    </Card>
);

const Dashboard: React.FC = () => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const { data } = await dashboardApi.getStats();
                setStats(data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
        const interval = setInterval(fetchStats, 300000); // Refresh every 5 minutes

        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (!stats) {
        return (
            <Typography variant="h6" color="error" align="center">
                Failed to load dashboard statistics
            </Typography>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom align="right">
                Stats last updated: {new Date(stats.timestamp).toLocaleString()}
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Total Users"
                        value={stats.total_users}
                        icon={<PeopleIcon sx={{ color: '#1976d2' }} />}
                        color="#1976d2"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Active Today"
                        value={stats.today_users}
                        icon={<GroupIcon sx={{ color: '#2e7d32' }} />}
                        color="#2e7d32"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="New Users"
                        value={stats.new_users}
                        icon={<PersonAddIcon sx={{ color: '#ed6c02' }} />}
                        color="#ed6c02"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Teams Created"
                        value={stats.teams_created}
                        icon={<GroupIcon sx={{ color: '#9c27b0' }} />}
                        color="#9c27b0"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Teams Synced"
                        value={stats.teams_synced}
                        icon={<SyncIcon sx={{ color: '#f57c00' }} />}
                        color="#f57c00"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Teams Match"
                        value={stats.matches_with_team}
                        icon={<ScoreIcon sx={{ color: '#388e3c' }} />}
                        color="#388e3c"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="App Installs"
                        value={stats.total_app_installed}
                        icon={<PhoneIcon sx={{ color: '#7b1fa2' }} />}
                        color="#7b1fa2"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Today's App Installs"
                        value={stats.today_app_installed}
                        icon={<MobileIcon sx={{ color: '#c2185b' }} />}
                        color="#c2185b"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Verified OTP"
                        value={stats.verification_otps_sent}
                        icon={<OtpIcon sx={{ color: '#0288d1' }} />}
                        color="#0288d1"
                    />
                </Grid>
                {/* Task Section */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Tasks"
                        value={stats.tasks}
                        icon={<AssignmentIcon sx={{ color: '#1976d2' }} />}
                        color="#1976d2"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Failed Tasks"
                        value={stats.failed_tasks}
                        icon={<ErrorIcon sx={{ color: '#d32f2f' }} />}
                        color="#d32f2f"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Pending Manual Actions"
                        value={stats.pending_manual_actions}
                        icon={<PendingIcon sx={{ color: '#ff9800' }} />}
                        color="#ff9800"
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;